/**
 * `useUsage` — workspace-level usage + plan limits.
 *
 * Reads from the `/v1/workspace` bootstrap response's `active_usage`
 * field. The server folds usage data into the bootstrap so the
 * dashboard's home page doesn't pay a second 2-3s round-trip for slot
 * counters and MTD billing — they ride on the same JWT verification
 * and DB connection as the rest of the workspace data.
 *
 * `/v1/usage` still exists as a targeted-refresh endpoint (e.g. after
 * a mutation enqueues a new audit and we want fresh MTD without
 * refetching the whole workspace blob), but the read path of this
 * hook never hits it. `invalidateUsage` therefore invalidates the
 * workspace query — that's the canonical source now.
 */
import type { QueryClient, UseQueryResult } from '@tanstack/react-query';
import type { UsageResponse } from '../api-types';
import { invalidateWorkspace, useWorkspace } from './workspace';

export function useUsage(): UseQueryResult<UsageResponse> {
  const workspace = useWorkspace();
  // Project the embedded slice into a UseQueryResult-shaped object so
  // callers using `.data`, `.isLoading`, `.error`, etc. work unchanged.
  // The cast is sound — every UseQueryResult field comes from
  // `workspace`; we only override `data` to the embedded slice.
  return {
    ...workspace,
    data: workspace.data?.active_usage ?? undefined,
  } as unknown as UseQueryResult<UsageResponse>;
}

export function invalidateUsage(qc: QueryClient): Promise<void> {
  // Usage lives on the workspace response now. Refresh the bootstrap
  // to pick up post-mutation MTD / slot counts. Callers that want to
  // refresh ONLY usage without re-fetching the workspace can hit
  // `/v1/usage` directly — but no caller does today.
  return invalidateWorkspace(qc);
}
